import './CalendarChart.scss';

import type { CalendarTooltipProps } from '@nivo/calendar';
import { ResponsiveCalendar } from '@nivo/calendar';
import { CustomEmpty } from 'components';
import dayjs from 'dayjs';
import { getNow } from 'helpers/timeHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import React from 'react';

import type { CompanyDailyStatistics, CompanyStatistics } from '../models';

interface OwnProps {
  data?: CompanyDailyStatistics;
}

interface CalendarDay extends CalendarTooltipProps {
  data: CompanyStatistics;
}

const CalendarChart = ({ data }: OwnProps) => {
  const { t } = useCustomTranslation();

  const currentYear = getNow().get('year');
  const startYear = data?.dailyStatistics.reduce((acc, x) => {
    const year = parseInt(x.day.substr(0, 4), 10);
    if (acc > year) return year;
    return acc;
  }, currentYear);

  const endYear = data?.dailyStatistics.reduce((acc, x) => {
    const year = parseInt(x.day.substr(0, 4), 10);
    if (acc < year) return year;
    return acc;
  }, currentYear);

  const finalData =
    data?.dailyStatistics
      .map((x) => ({
        ...x,
        value: x.users + x.customers + x.serviceObjects + x.orders + x.completeOrders
      }))
      .filter((x) => x.value > 0) || [];

  const noData = finalData.length === 0;

  if (!startYear || !endYear) return null;
  // Each year 200px height
  const height = (endYear - startYear + 1) * 250;

  return (
    <div className="CalendarChart" style={{ height }}>
      {noData ? (
        <CustomEmpty />
      ) : (
        <ResponsiveCalendar
          from={`${startYear}-01-01`}
          to={`${endYear}-12-31`}
          data={finalData}
          emptyColor="#eeeeee"
          colors={['#61cdbb', '#97e3d5', '#e8c1a0', '#f47560']}
          margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
          monthBorderColor="#ffffff"
          monthLegend={(year, month) => dayjs().month(month).format('MMM')}
          tooltip={(i) => {
            const item = i as CalendarDay;
            return (
              <div className="CalendarChart_tooltip-container">
                <div className="CalendarChart_tooltip-day">
                  {dayjs.utc(item.data.day).format('L')}
                </div>
                <div className="CalendarChart_tooltip-pair">
                  <div className="CalendarChart_tooltip-label">
                    {t('dashboard:calendarChart_orders')}:
                  </div>
                  <div className="CalendarChart_tooltip-value">{item.data.orders}</div>
                </div>
                <div className="CalendarChart_tooltip-pair">
                  <div className="CalendarChart_tooltip-label">
                    {t('dashboard:calendarChart_completeOrders')}:
                  </div>
                  <div className="CalendarChart_tooltip-value">{item.data.completeOrders}</div>
                </div>
                <div className="CalendarChart_tooltip-pair">
                  <div className="CalendarChart_tooltip-label">
                    {t('dashboard:calendarChart_users')}:
                  </div>
                  <div className="CalendarChart_tooltip-value">{item.data.users}</div>
                </div>
                <div className="CalendarChart_tooltip-pair">
                  <div className="CalendarChart_tooltip-label">
                    {t('dashboard:calendarChart_customers')}:
                  </div>
                  <div className="CalendarChart_tooltip-value">{item.data.customers}</div>
                </div>
                <div className="CalendarChart_tooltip-pair">
                  <div className="CalendarChart_tooltip-label">
                    {t('dashboard:calendarChart_serviceObjects')}:
                  </div>
                  <div className="CalendarChart_tooltip-value">{item.data.serviceObjects}</div>
                </div>
                <div className="CalendarChart_tooltip-pair">
                  <div className="CalendarChart_tooltip-label">
                    {t('dashboard:calendarChart_plannedInterventions')}:
                  </div>
                  <div className="CalendarChart_tooltip-value">
                    {item.data.plannedInterventions}
                  </div>
                </div>
                <div className="CalendarChart_tooltip-pair">
                  <div className="CalendarChart_tooltip-label">
                    {t('dashboard:calendarChart_completeInterventions')}:
                  </div>
                  <div className="CalendarChart_tooltip-value">
                    {item.data.completeInterventions}
                  </div>
                </div>
              </div>
            );
          }}
          dayBorderWidth={2}
          dayBorderColor="#ffffff"
        />
      )}
    </div>
  );
};

export default CalendarChart;
